import qs from 'qs'

// import { redirectToLogin } from 'app/loginRedirect/loginRedirectHelpers'

import { PEACH_UTM } from 'core/actions/helpers/tokenHelpers'
import { authStore, authActions } from 'core/auth/authStore'
import { assert } from 'core/helpers/assert'
import * as R from 'core/helpers/remeda'
import { retrieveSession } from 'core/hooks/useStoredState/storage'
import {
  envStore,
  STANDARD_ENVIRONMENTS,
  Env,
  StandardEnvConfig,
} from 'core/stores/env'

import rawApiCall, { FetchOptions, parseErrorResponse } from './rawApiCall'

// Helper function to get the current API base
const getCurrentApiBase = (): string => {
  let resolvedActiveEnv: Env | undefined
  const currentActiveRef = envStore?.state?.activeEnvRef

  if (currentActiveRef) {
    if (currentActiveRef.type === 'standard') {
      const foundStandardEnv: StandardEnvConfig | undefined =
        STANDARD_ENVIRONMENTS.find((e) => e.id === currentActiveRef.id)
      if (foundStandardEnv) {
        resolvedActiveEnv = {
          ...foundStandardEnv,
          key: foundStandardEnv.id,
          isStandard: true,
          label: foundStandardEnv.label || '',
          companyId: foundStandardEnv.companyId || '',
          userType: foundStandardEnv.userType || '',
          username: '',
        }
      }
    } else if (currentActiveRef.type === 'custom') {
      const foundCustomEnv = envStore?.state?.customEnvs?.find(
        (e) => e.id === currentActiveRef.id,
      )
      if (foundCustomEnv) {
        resolvedActiveEnv = { ...foundCustomEnv, isStandard: false }
      }
    }
  }
  return resolvedActiveEnv?.apiBase || ''
}

const unauthorized = () => {
  authActions.handleUnauthorizedAccess()
  // redirectToLogin()
}

// In src/core/api/peachApi.ts

const peachApiCall = ({ url, headers, ...restOptions }: FetchOptions) => {
  const baseHeaders: { [key: string]: string } = {
    'Peach-Version': import.meta.env.VITE_PEACH_API_VERSION ?? '2023-11-29',
    ...(retrieveSession(PEACH_UTM) ?? {}),
  }

  if (authStore?.state?.apiKey) {
    baseHeaders['X-API-KEY'] = authStore.state.apiKey
  } else if (authStore?.state?.token) {
    baseHeaders.Authorization = `Bearer ${authStore.state.token}`
  }

  const apiBase = getCurrentApiBase()
  const finalUrl = url.startsWith('http') ? url : apiBase + url
  const finalHeaders = { ...baseHeaders, ...headers }

  try {
    const result = rawApiCall({
      url: finalUrl,
      headers: finalHeaders,
      ...restOptions,
    })
    return result
  } catch (error) {
    if (R.isObject(error) && 'status' in error && error.status === 401) {
      unauthorized()
    }
    throw error
  }
}

const peachApi = {
  fetch: peachApiCall,
  get: (options: FetchOptions) => peachApiCall({ ...options, method: 'GET' }),
  post: (options: FetchOptions) => peachApiCall({ ...options, method: 'POST' }),
  put: (options: FetchOptions) => peachApiCall({ ...options, method: 'PUT' }),
  patch: (options: FetchOptions) =>
    peachApiCall({ ...options, method: 'PATCH' }),
  delete: (options: FetchOptions) =>
    peachApiCall({ ...options, method: 'DELETE' }),

  /** @deprecated */
  getAll: async (options: FetchOptions, result: any = {}, depth = 0) => {
    const resp = await peachApiCall({ ...options, method: 'GET' })

    assert(R.isArray(resp?.data), 'Must return a paginated list.')

    const newResult = {
      ...result,
      ...resp,
      count: (result?.count ?? 0) + resp.count,
      data: (result?.data ?? []).concat(resp.data),
    }

    if (resp.nextUrl) {
      assert(depth < 10, 'Must be less than 10 pages')

      return peachApi.getAll(
        { ...options, url: resp.nextUrl },
        newResult,
        depth + 1,
      )
    }

    return newResult
  },

  /**
   * Use this when needing to directly download a file that requires authorization.
   * Create a temporary a-tag with href set to a fancy url that actually has all
   * the data embedded into it, then programmatically click it. This triggers the
   * browser's file download behavior to kick in. The "download" attribute is set
   * so that the browser knows what the default filename is.
   */
  download: async ({
    url,
    query,
    ...restOptions
  }: Omit<FetchOptions, 'body'>) => {
    const token = authStore.state.token

    if (!token) {
      unauthorized()
      return
    }

    const apiBase = getCurrentApiBase()
    if (!apiBase) {
      unauthorized()
      return
    }

    const response = await fetch(
      apiBase +
        url +
        qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'comma' }),
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        ...restOptions,
      },
    )

    if (response.ok) {
      const a = document.createElement('a')
      a.setAttribute(
        'href',
        `data:application/octet-stream,${await response.text()}`,
      )
      a.setAttribute(
        'download',
        R.find(
          (response.headers.get('Content-Disposition') || '').split(';'),
          (item) => item.trim().startsWith('filename='),
        )
          ?.trim()
          .split('=')[1] ??
          R.last(url.split('/')) ??
          '',
      )
      a.click()
      a.remove()
    } else {
      if (response.status === 401) {
        unauthorized()
      } else {
        const error = await parseErrorResponse(response)
        throw error
      }
    }
  },
}

export default peachApi
